import { useEffect, useState, useCallback } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Plus, Trash2, ExternalLink, Pencil, ImagePlus, Save, Copy, Eye, Upload, FileUp, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import Header from "../components/dashboard/Header";
import { useAuth } from "../context/AuthContext";
import api, { formatApiErrorDetail } from "../lib/api";
import { fmtCurrency } from "../lib/format";

const inputCls = "w-full bg-brand-page border border-brand-border rounded-md px-3 py-2 text-sm text-brand-ink focus:outline-none focus:border-brand-primary";
const labelCls = "block text-[10px] uppercase tracking-[0.15em] text-brand-tertiary mb-1";

function Tab({ id, active, onClick, children }) {
  return <button onClick={() => onClick(id)} data-testid={`proposals-tab-${id}`}
    className={`px-4 py-2 text-sm border-b-2 transition-colors ${active ? "border-brand-primary text-brand-ink" : "border-transparent text-brand-muted hover:text-brand-ink"}`}>{children}</button>;
}

function isExpired(v) {
  if (!v) return false;
  const d = new Date(v);
  return !isNaN(d.getTime()) && d < new Date(new Date().toDateString());
}

const fmtDate = (s) => {
  if (!s) return "—";
  const d = new Date(s);
  return isNaN(d.getTime()) ? "—" : d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

function StatusBadge({ o }) {
  if (o.source !== "yardi" && !o.status) return <span className="text-[11px] text-brand-tertiary">—</span>;
  const map = {
    available: { label: "Available", cls: "bg-brand-pos/15 text-brand-pos border-brand-pos/30" },
    notice: { label: "On notice", cls: "bg-brand-gold/15 text-brand-gold border-brand-gold/40" },
    occupied: { label: "Occupied", cls: "bg-brand-hover text-brand-tertiary border-brand-border" },
  };
  const s = map[o.status] || map.occupied;
  const fmtDate = (iso) => { try { const [y, m, d] = iso.split("-").map(Number); return new Date(y, m - 1, d).toLocaleDateString("en-US", { month: "short", day: "numeric" }); } catch { return iso; } };
  return (
    <span className="inline-flex flex-col gap-0.5" data-testid={`office-status-${o.office_number}`}>
      <span className={`inline-flex items-center w-fit text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 rounded-full border ${s.cls}`}>{s.label}</span>
      {o.status === "notice" && o.notice_date && <span className="text-[10px] text-brand-tertiary">frees {fmtDate(o.notice_date)}</span>}
      {o.status === "occupied" && o.occupant && <span className="text-[10px] text-brand-tertiary truncate max-w-[140px]" title={o.occupant}>{o.occupant}</span>}
    </span>
  );
}

export function OfficeCatalog({ locations }) {
  const [offices, setOffices] = useState([]);
  const blank = { location: locations[0]?.location || "", office_number: "", desks: "", window: false, conference_credits: "", monthly_price: "", price_6mo: "", price_12mo: "", photo_url: null };
  const [form, setForm] = useState(blank);
  const [editId, setEditId] = useState(null);
  const [filterLoc, setFilterLoc] = useState("all");
  const load = useCallback(() => api.get("/proposals/offices").then(({ data }) => setOffices(data.offices || [])), []);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const auto6 = form.monthly_price ? Math.round(Number(form.monthly_price) * 0.95 * 100) / 100 : "";
  const auto12 = form.monthly_price ? Math.round(Number(form.monthly_price) * 0.90 * 100) / 100 : "";
  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/proposals/media", fd); set("photo_url", data.url); toast.success("Photo uploaded"); }
    catch (err) { toast.error("Upload failed", { description: formatApiErrorDetail(err.response?.data?.detail) }); }
  };
  const save = async () => {
    if (!form.office_number.trim()) { toast.error("Office number required"); return; }
    const payload = {
      location: form.location, office_number: form.office_number.trim(),
      desks: form.desks === "" ? null : Number(form.desks), window: !!form.window,
      conference_credits: form.conference_credits === "" ? null : Number(form.conference_credits),
      monthly_price: Number(form.monthly_price) || 0,
      price_6mo: form.price_6mo === "" ? null : Number(form.price_6mo),
      price_12mo: form.price_12mo === "" ? null : Number(form.price_12mo),
      photo_url: form.photo_url,
    };
    try {
      if (editId) await api.put(`/proposals/offices/${editId}`, payload);
      else await api.post("/proposals/offices", payload);
      toast.success("Office saved"); setForm(blank); setEditId(null); load();
    } catch (err) { toast.error("Save failed", { description: formatApiErrorDetail(err.response?.data?.detail) }); }
  };
  const edit = (o) => { setEditId(o.id); setForm({ ...o, desks: o.desks ?? "", conference_credits: o.conference_credits ?? "", price_6mo: o.price_6mo ?? "", price_12mo: o.price_12mo ?? "" }); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const del = async (o) => { if (!window.confirm(`Delete Office ${o.office_number}?`)) return; await api.delete(`/proposals/offices/${o.id}`); load(); };
  const [importing, setImporting] = useState(false);
  const [clearing, setClearing] = useState(false);
  const importSheet = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setImporting(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/proposals/offices/import", fd);
      toast.success(`Loaded ${data.imported} offices`, { description: `Removed ${data.removed} existing • ${data.locations.join(", ")}` });
      load();
    } catch (err) { toast.error("Import failed", { description: formatApiErrorDetail(err.response?.data?.detail) }); }
    finally { setImporting(false); e.target.value = ""; }
  };
  const clearAll = async () => {
    if (!window.confirm("Remove ALL offices from the catalog? This cannot be undone. Existing proposals keep their saved copies.")) return;
    setClearing(true);
    try {
      const { data } = await api.delete("/proposals/offices");
      toast.success(`Cleared ${data.removed} offices`);
      load();
    } catch (err) { toast.error("Clear failed", { description: formatApiErrorDetail(err.response?.data?.detail) }); }
    finally { setClearing(false); }
  };
  const [syncing, setSyncing] = useState(false);
  const syncYardi = async () => {
    setSyncing(true);
    try {
      const { data: start } = await api.post("/proposals/offices/sync-yardi");
      const locs = start.locations || [];
      toast.loading(`Syncing ${locs.length} locations from Yardi…`, { id: "yardi-sync" });
      await new Promise((resolve) => {
        const poll = setInterval(async () => {
          try {
            const { data: p } = await api.get("/proposals/offices/sync-progress");
            const doneCount = (p.done || []).length;
            if (p.running) {
              toast.loading(`Syncing ${p.current || "…"} (${doneCount}/${p.total})`, { id: "yardi-sync" });
            } else if (p.finished_at) {
              clearInterval(poll);
              const totals = (p.done || []).reduce((a, r) => ({
                offices: a.offices + (r.offices || 0), available: a.available + (r.available || 0), on_notice: a.on_notice + (r.on_notice || 0),
              }), { offices: 0, available: 0, on_notice: 0 });
              const errs = (p.done || []).filter((r) => r.error);
              toast.success(`Synced ${totals.offices} offices`, { id: "yardi-sync", description: `${totals.available} available · ${totals.on_notice} on notice${errs.length ? ` · ${errs.length} failed` : ""}` });
              load();
              resolve();
            }
          } catch { /* keep polling */ }
        }, 3000);
      });
    } catch (err) {
      const msg = err.response?.status === 409 ? "A sync is already running." : formatApiErrorDetail(err.response?.data?.detail);
      toast.error("Sync failed", { id: "yardi-sync", description: msg });
    } finally { setSyncing(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 bg-brand-hover border border-brand-border px-5 py-3">
        <p className="text-sm text-brand-muted"><strong>Sync from Yardi</strong> pulls Private Offices live — retail (month-to-month) price + availability &amp; on-notice status. 6-mo/12-mo tiers auto-calc (−5% / −10%); Coworking is $300/mo flat. Or bulk-load from a spreadsheet.</p>
        <div className="flex items-center gap-2 shrink-0">
          <button onClick={syncYardi} disabled={syncing} data-testid="office-sync-yardi"
            className={`inline-flex items-center gap-2 text-sm px-4 py-2 rounded-md ${syncing ? "bg-brand-border text-brand-tertiary cursor-wait" : "bg-brand-primary text-brand-page hover:bg-brand-primaryHover cursor-pointer"}`}>
            <RefreshCw className={`h-4 w-4 ${syncing ? "animate-spin" : ""}`} /> {syncing ? "Syncing…" : "Sync from Yardi"}
          </button>
          <button onClick={clearAll} disabled={clearing || offices.length === 0} data-testid="office-clear-all"
            className={`inline-flex items-center gap-2 text-sm px-4 py-2 rounded-md border ${clearing || offices.length === 0 ? "border-brand-border text-brand-tertiary cursor-not-allowed" : "border-brand-neg text-brand-neg hover:bg-brand-neg/10 cursor-pointer"}`}>
            <Trash2 className="h-4 w-4" /> {clearing ? "Clearing…" : "Clear all"}
          </button>
          <label className={`inline-flex items-center gap-2 text-sm px-4 py-2 rounded-md cursor-pointer ${importing ? "bg-brand-border text-brand-tertiary" : "bg-brand-ink text-brand-page hover:bg-brand-primary"}`}>
            <Upload className="h-4 w-4" /> {importing ? "Importing…" : "Import sheet"}
            <input type="file" accept=".xlsx,.xlsm" className="hidden" onChange={importSheet} data-testid="office-import" disabled={importing} />
          </label>
        </div>
      </div>

      <div className="bg-brand-surface border border-brand-border p-5" data-testid="office-form">
        <p className="font-serif text-lg text-brand-ink mb-4">{editId ? "Edit office" : "Add office to catalog"}</p>
        <div className="grid sm:grid-cols-3 gap-4">
          <div><label className={labelCls}>Location</label>
            <select className={inputCls} value={form.location} onChange={(e) => set("location", e.target.value)} data-testid="office-location">
              {locations.map((l) => <option key={l.location} value={l.location}>{l.location}</option>)}
            </select></div>
          <div><label className={labelCls}>Office / Suite #</label><input className={inputCls} value={form.office_number} onChange={(e) => set("office_number", e.target.value)} data-testid="office-number" /></div>
          <div><label className={labelCls}>Desks</label><input type="number" className={inputCls} value={form.desks} onChange={(e) => set("desks", e.target.value)} data-testid="office-desks" /></div>
          <div><label className={labelCls}>Conference Credits</label><input type="number" className={inputCls} value={form.conference_credits} onChange={(e) => set("conference_credits", e.target.value)} data-testid="office-credits" /></div>
          <div className="flex items-end"><label className="inline-flex items-center gap-2 text-sm text-brand-ink"><input type="checkbox" checked={form.window} onChange={(e) => set("window", e.target.checked)} className="h-4 w-4 accent-brand-primary" data-testid="office-window" /> Has window</label></div>
          <div><label className={labelCls}>Photo</label>
            <label className="inline-flex items-center gap-2 text-sm text-brand-primary cursor-pointer"><ImagePlus className="h-4 w-4" /> {form.photo_url ? "Replace" : "Upload"}<input type="file" accept="image/*" className="hidden" onChange={uploadPhoto} data-testid="office-photo" /></label></div>
          <div><label className={labelCls}>Monthly (M2M) $</label><input type="number" className={inputCls} value={form.monthly_price} onChange={(e) => set("monthly_price", e.target.value)} data-testid="office-monthly" /></div>
          <div><label className={labelCls}>6-Mo $ <span className="text-brand-tertiary normal-case">(auto {auto6 ? fmtCurrency(auto6) : "−5%"})</span></label><input type="number" className={inputCls} value={form.price_6mo} onChange={(e) => set("price_6mo", e.target.value)} placeholder={auto6 ? String(auto6) : ""} data-testid="office-6mo" /></div>
          <div><label className={labelCls}>12-Mo $ <span className="text-brand-tertiary normal-case">(auto {auto12 ? fmtCurrency(auto12) : "−10%"})</span></label><input type="number" className={inputCls} value={form.price_12mo} onChange={(e) => set("price_12mo", e.target.value)} placeholder={auto12 ? String(auto12) : ""} data-testid="office-12mo" /></div>
        </div>
        <div className="flex gap-2 mt-4">
          <button onClick={save} data-testid="office-save" className="inline-flex items-center gap-2 bg-brand-primary text-brand-page px-4 py-2 text-sm hover:bg-brand-primaryHover"><Save className="h-4 w-4" /> {editId ? "Update office" : "Add office"}</button>
          {editId && <button onClick={() => { setForm(blank); setEditId(null); }} className="px-4 py-2 text-sm border border-brand-border text-brand-muted">Cancel</button>}
        </div>
      </div>

      {(() => {
        const byLoc = offices.reduce((m, o) => { (m[o.location] = m[o.location] || []).push(o); return m; }, {});
        const locNames = Object.keys(byLoc).sort();
        const shown = filterLoc === "all" ? locNames : locNames.filter((l) => l === filterLoc);
        return (
          <>
            <div className="flex flex-wrap items-center gap-3 bg-brand-surface border border-brand-border px-5 py-3" data-testid="office-catalog-toolbar">
              <span className="text-sm text-brand-muted">{offices.length} offices across {locNames.length} locations</span>
              <div className="ml-auto flex items-center gap-2">
                <label className="text-[10px] uppercase tracking-[0.15em] text-brand-tertiary">Location</label>
                <select value={filterLoc} onChange={(e) => setFilterLoc(e.target.value)} data-testid="office-location-filter"
                  className="bg-brand-page border border-brand-border rounded-md px-3 py-1.5 text-sm text-brand-ink focus:outline-none focus:border-brand-primary">
                  <option value="all">All locations</option>
                  {locNames.map((l) => <option key={l} value={l}>{l} ({byLoc[l].length})</option>)}
                </select>
              </div>
            </div>

            {offices.length === 0 && (
              <div className="bg-brand-surface border border-brand-border px-6 py-8 text-center text-brand-tertiary">No offices yet. Add your first above or import a spreadsheet.</div>
            )}

            {shown.map((loc) => (
              <div key={loc} className="bg-brand-surface border border-brand-border overflow-hidden" data-testid={`office-group-${loc}`}>
                <div className="flex items-center justify-between gap-3 px-6 py-3 bg-brand-hover border-b border-brand-border sticky top-0">
                  <h3 className="font-serif text-lg text-brand-ink">{loc}</h3>
                  <span className="text-[11px] uppercase tracking-[0.15em] text-brand-tertiary">{byLoc[loc].length} offices</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="text-brand-tertiary border-b border-brand-border">{["Office", "Status", "Desks", "Window", "Credits", "M2M", "6-Mo", "12-Mo", ""].map((h, i) => <th key={h || "a"} className={`font-medium uppercase tracking-[0.1em] text-[11px] px-4 py-2.5 ${i === 0 ? "text-left px-6" : i === 1 ? "text-left" : "text-right"}`}>{h}</th>)}</tr></thead>
                    <tbody>
                      {byLoc[loc].map((o) => (
                        <tr key={o.id} data-testid={`office-row-${o.office_number}`} className="border-b border-brand-border last:border-0 hover:bg-brand-hover">
                          <td className="px-6 py-3 text-brand-ink">{o.office_number}</td>
                          <td className="px-4 py-3"><StatusBadge o={o} /></td>
                          <td className="px-4 py-3 text-right font-mono text-brand-muted">{o.desks ?? "—"}</td>
                          <td className="px-4 py-3 text-right text-brand-muted">{o.window ? "Yes" : "—"}</td>
                          <td className="px-4 py-3 text-right font-mono text-brand-muted">{o.conference_credits ?? "—"}</td>
                          <td className="px-4 py-3 text-right font-mono text-brand-ink">{fmtCurrency(o.monthly_price)}</td>
                          <td className="px-4 py-3 text-right font-mono text-brand-muted">{fmtCurrency(o.price_6mo)}</td>
                          <td className="px-4 py-3 text-right font-mono text-brand-primary">{fmtCurrency(o.price_12mo)}</td>
                          <td className="px-4 py-3 text-right whitespace-nowrap">
                            <button onClick={() => edit(o)} data-testid={`office-edit-${o.office_number}`} className="p-1.5 text-brand-tertiary hover:text-brand-primary"><Pencil className="h-4 w-4" /></button>
                            <button onClick={() => del(o)} data-testid={`office-delete-${o.office_number}`} className="p-1.5 text-brand-tertiary hover:text-brand-neg"><Trash2 className="h-4 w-4" /></button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </>
        );
      })()}
    </div>
  );
}

export function LocationLibrary({ locations, reload }) {
  const [sel, setSel] = useState(locations[0]?.location || "");
  const cur = locations.find((l) => l.location === sel);
  const [form, setForm] = useState(cur || {});
  useEffect(() => { setForm(cur || {}); }, [sel]); // eslint-disable-line
  if (!cur) return <p className="text-sm text-brand-tertiary">No locations.</p>;
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const uploadImg = async (e, kind) => {
    const file = e.target.files?.[0]; if (!file) return;
    const isPdf = file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
    const fd = new FormData(); fd.append("file", file);
    const tid = toast.loading(isPdf ? "Converting PDF…" : "Uploading…");
    try {
      const { data } = await api.post("/proposals/media", fd);
      const raw = data.items || [{ id: data.url.split("/").pop(), url: data.url, pdf: data.pdf }];
      const newImgs = raw.map((it) => ({ id: it.id || it.url.split("/").pop(), url: it.url, caption: "", kind, pdf: !!it.pdf }));
      const images = [...(form.images || []), ...newImgs];
      // Persist immediately so uploaded floor plans / photos always stick (no separate Save needed).
      await api.put(`/proposals/locations/${encodeURIComponent(sel)}`, {
        location: sel, email: form.email || "", address: form.address || "", city: form.city || "", description: form.description || "",
        amenities: (typeof form.amenities === "string" ? form.amenities.split(",").map((s) => s.trim()).filter(Boolean) : (form.amenities || [])),
        conference_rooms: form.conference_rooms || "", images,
      });
      set("images", images);
      reload();
      toast.success(`${newImgs.length} ${kind === "floorplan" ? (newImgs.length > 1 ? "floor plan pages" : "floor plan") : "photo"} added & saved`, { id: tid });
    } catch (err) { toast.error("Upload failed", { id: tid, description: formatApiErrorDetail(err.response?.data?.detail) }); }
    finally { e.target.value = ""; }
  };
  const removeImg = (url) => set("images", (form.images || []).filter((i) => i.url !== url));
  const save = async () => {
    try {
      await api.put(`/proposals/locations/${encodeURIComponent(sel)}`, {
        location: sel, email: form.email || "", address: form.address || "", city: form.city || "", description: form.description || "",
        amenities: (typeof form.amenities === "string" ? form.amenities.split(",").map((s) => s.trim()).filter(Boolean) : (form.amenities || [])),
        conference_rooms: form.conference_rooms || "", images: form.images || [],
      });
      toast.success("Location saved"); reload();
    } catch (err) { toast.error("Save failed", { description: formatApiErrorDetail(err.response?.data?.detail) }); }
  };
  const amenitiesStr = Array.isArray(form.amenities) ? form.amenities.join(", ") : (form.amenities || "");

  return (
    <div className="grid lg:grid-cols-[220px_1fr] gap-6">
      <div className="flex lg:flex-col gap-1 flex-wrap">
        {locations.map((l) => (
          <button key={l.location} onClick={() => setSel(l.location)} data-testid={`libloc-${l.location}`}
            className={`text-left text-sm px-3 py-2 rounded-md ${sel === l.location ? "bg-brand-primary text-brand-page" : "text-brand-muted hover:bg-brand-hover"}`}>{l.location}</button>
        ))}
      </div>
      <div className="bg-brand-surface border border-brand-border p-5 space-y-4" data-testid="lib-form">
        <div className="grid sm:grid-cols-2 gap-4">
          <div><label className={labelCls}>City</label><input className={inputCls} value={form.city || ""} onChange={(e) => set("city", e.target.value)} data-testid="lib-city" /></div>
          <div><label className={labelCls}>Address</label><input className={inputCls} value={form.address || ""} onChange={(e) => set("address", e.target.value)} data-testid="lib-address" /></div>
        </div>
        <div><label className={labelCls}>Location email (used as Reply-To on prospect emails)</label><input type="email" className={inputCls} value={form.email || ""} onChange={(e) => set("email", e.target.value)} placeholder="team@bondcollective.com" data-testid="lib-email" /></div>
        <div><label className={labelCls}>Description</label><textarea rows={2} className={inputCls} value={form.description || ""} onChange={(e) => set("description", e.target.value)} data-testid="lib-description" /></div>
        <div><label className={labelCls}>Amenities (comma-separated)</label><textarea rows={2} className={inputCls} value={amenitiesStr} onChange={(e) => set("amenities", e.target.value)} data-testid="lib-amenities" /></div>
        <div><label className={labelCls}>Conference Rooms</label><textarea rows={2} className={inputCls} value={form.conference_rooms || ""} onChange={(e) => set("conference_rooms", e.target.value)} data-testid="lib-conference" /></div>
        <div className="flex flex-wrap gap-4">
          <label className="inline-flex items-center gap-2 text-sm text-brand-primary cursor-pointer"><ImagePlus className="h-4 w-4" /> Add photo<input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImg(e, "photo")} data-testid="lib-add-photo" /></label>
          <label className="inline-flex items-center gap-2 text-sm text-brand-primary cursor-pointer"><FileUp className="h-4 w-4" /> Add floor plan (PDF or image)<input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => uploadImg(e, "floorplan")} data-testid="lib-add-floorplan" /></label>
        </div>
        {(form.images || []).length > 0 && (
          <div className="flex flex-wrap gap-2">
            {(form.images || []).map((im) => (
              <div key={im.url} className="relative">
                <img src={im.url.startsWith("http") ? im.url : `${process.env.REACT_APP_BACKEND_URL}${im.url}`} alt="" className="h-20 w-20 object-cover border border-brand-border" />
                <span className="absolute bottom-0 left-0 text-[8px] uppercase bg-brand-ink/70 text-brand-page px-1">{im.kind}</span>
                <button onClick={() => removeImg(im.url)} className="absolute -top-2 -right-2 bg-brand-neg text-white rounded-full h-5 w-5 flex items-center justify-center text-xs">×</button>
              </div>
            ))}
          </div>
        )}
        <button onClick={save} data-testid="lib-save" className="inline-flex items-center gap-2 bg-brand-primary text-brand-page px-4 py-2 text-sm hover:bg-brand-primaryHover"><Save className="h-4 w-4" /> Save location</button>
      </div>
    </div>
  );
}

export function SlackAlerts() {
  const [data, setData] = useState({ mappings: {}, default: "", locations: [] });
  const [saving, setSaving] = useState(false);
  const load = useCallback(() => api.get("/proposals/slack-settings").then(({ data }) => setData(data)), []);
  useEffect(() => { load(); }, [load]);

  const setMap = (loc, v) => setData((d) => ({ ...d, mappings: { ...d.mappings, [loc]: v } }));
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/proposals/slack-settings", { default: data.default || "", mappings: data.mappings || {} });
      toast.success("Slack channels saved");
      load();
    } catch (err) { toast.error("Could not save", { description: formatApiErrorDetail(err.response?.data?.detail) }); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-3xl" data-testid="slack-alerts">
      <div className="rounded-lg border border-brand-border bg-brand-surface p-5 mb-6">
        <p className="text-sm text-brand-ink font-medium">Route agreement requests to Slack</p>
        <p className="text-[13px] text-brand-muted mt-1 leading-relaxed">
          When a prospect requests an agreement, we'll post an alert to the Slack channel(s) for the office location(s) they picked. Paste a Slack <strong>Incoming Webhook URL</strong> next to each location. Anything left blank uses the default channel.
        </p>
        <p className="text-[12px] text-brand-tertiary mt-2">
          Create webhooks at api.slack.com/apps → your app → Incoming Webhooks → Add New Webhook to Workspace (one per channel).
        </p>
      </div>

      <div className="mb-6">
        <label className={labelCls}>Default channel (fallback for any unmapped location)</label>
        <input value={data.default || ""} onChange={(e) => setData((d) => ({ ...d, default: e.target.value }))}
          placeholder="https://hooks.slack.com/services/T000/B000/xxxx" data-testid="slack-default-webhook"
          className={`${inputCls} font-mono text-[12px]`} />
      </div>

      <div className="border border-brand-border rounded-lg divide-y divide-brand-border">
        {(data.locations || []).map((loc) => (
          <div key={loc} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 px-4 py-3">
            <span className="text-sm text-brand-ink font-medium w-40 shrink-0">{loc}</span>
            <input value={data.mappings?.[loc] || ""} onChange={(e) => setMap(loc, e.target.value)}
              placeholder="Uses default channel" data-testid={`slack-webhook-${loc}`}
              className={`${inputCls} font-mono text-[12px] flex-1`} />
          </div>
        ))}
        {(data.locations || []).length === 0 && <p className="px-4 py-6 text-sm text-brand-tertiary">No locations yet — add them in the Locations tab.</p>}
      </div>

      <button onClick={save} disabled={saving} data-testid="slack-save"
        className="mt-6 inline-flex items-center gap-2 bg-brand-primary text-brand-page px-5 py-2.5 text-sm hover:bg-brand-primaryHover disabled:opacity-60">
        <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save channels"}
      </button>
    </div>
  );
}

export default function Proposals() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("proposals");
  const [proposals, setProposals] = useState([]);
  const [locations, setLocations] = useState([]);
  const [pFilterLoc, setPFilterLoc] = useState(() => new URLSearchParams(window.location.search).get("location") || "all");
  const [pFilterStatus, setPFilterStatus] = useState("all");
  const [pSearch, setPSearch] = useState("");

  const loadLocs = useCallback(() => api.get("/proposals/locations").then(({ data }) => setLocations(data.locations || [])), []);
  const loadProps = useCallback(() => api.get("/proposals").then(({ data }) => setProposals(data.proposals || [])), []);
  useEffect(() => { if (["admin", "regional", "community"].includes(user?.role)) { loadLocs(); loadProps(); } }, [user, loadLocs, loadProps]);

  if (user && !["admin", "regional", "community"].includes(user.role)) return <Navigate to="/" replace />;

  const create = async () => {
    const loc = pFilterLoc !== "all" ? pFilterLoc : null;
    try {
      const { data } = await api.post("/proposals", { title: "Untitled Proposal", locations: loc ? [loc] : [], office_ids: [] });
      navigate(`/proposals/${data.id}${loc ? `?from=${encodeURIComponent(loc)}` : ""}`);
    }
    catch (err) { toast.error("Could not create", { description: formatApiErrorDetail(err.response?.data?.detail) }); }
  };
  const del = async (p) => { if (!window.confirm(`Delete "${p.title}"?`)) return; await api.delete(`/proposals/${p.id}`); loadProps(); };
  const duplicate = async (p) => {
    try { const { data } = await api.post(`/proposals/${p.id}/duplicate`); toast.success("Proposal duplicated"); navigate(`/proposals/${data.id}`); }
    catch (err) { toast.error("Could not duplicate", { description: formatApiErrorDetail(err.response?.data?.detail) }); }
  };

  return (
    <div className="min-h-screen bg-brand-page relative z-10">
      <Header />
      <main className="max-w-[1600px] mx-auto px-6 lg:px-12 py-10 space-y-6">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-brand-tertiary">Sales Tools</p>
            <h1 className="font-serif text-4xl lg:text-5xl font-light tracking-tight text-brand-ink mt-2">Proposals</h1>
            <p className="text-sm text-brand-muted mt-1">Build high-end, shareable workspace proposals for prospects.</p>
          </div>
          <button onClick={create} data-testid="proposal-new" className="flex items-center gap-2 bg-brand-primary text-brand-page px-5 py-2.5 text-sm hover:bg-brand-primaryHover self-start"><Plus className="h-4 w-4" /> New Proposal</button>
        </div>

        <div className="flex gap-2 border-b border-brand-border">
          <Tab id="proposals" active={tab === "proposals"} onClick={setTab}>Proposals</Tab>
        </div>

        {tab === "proposals" && (() => {
          const allLocs = Array.from(new Set([...proposals.flatMap((p) => p.locations || []), ...(pFilterLoc !== "all" ? [pFilterLoc] : [])])).sort();
          const filtered = proposals.filter((p) => {
            if (pFilterLoc !== "all" && !(p.locations || []).includes(pFilterLoc)) return false;
            const eff = p.status === "published" && isExpired(p.valid_until) ? "expired" : p.status;
            if (pFilterStatus !== "all" && eff !== pFilterStatus) return false;
            if (pSearch.trim() && !`${p.title} ${p.client_name || ""}`.toLowerCase().includes(pSearch.toLowerCase())) return false;
            return true;
          });
          return (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-3 bg-brand-surface border border-brand-border px-5 py-3" data-testid="proposals-toolbar">
                <input value={pSearch} onChange={(e) => setPSearch(e.target.value)} placeholder="Search title or client…" data-testid="proposals-search"
                  className="bg-brand-page border border-brand-border rounded-md px-3 py-1.5 text-sm text-brand-ink focus:outline-none focus:border-brand-primary w-56" />
                <div className="flex items-center gap-2">
                  <label className="text-[10px] uppercase tracking-[0.15em] text-brand-tertiary">Location</label>
                  <select value={pFilterLoc} onChange={(e) => setPFilterLoc(e.target.value)} data-testid="proposals-location-filter"
                    className="bg-brand-page border border-brand-border rounded-md px-3 py-1.5 text-sm text-brand-ink focus:outline-none focus:border-brand-primary">
                    <option value="all">All locations</option>
                    {allLocs.map((l) => <option key={l} value={l}>{l}</option>)}
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-[10px] uppercase tracking-[0.15em] text-brand-tertiary">Status</label>
                  <select value={pFilterStatus} onChange={(e) => setPFilterStatus(e.target.value)} data-testid="proposals-status-filter"
                    className="bg-brand-page border border-brand-border rounded-md px-3 py-1.5 text-sm text-brand-ink focus:outline-none focus:border-brand-primary">
                    <option value="all">All statuses</option>
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                    <option value="expired">Expired</option>
                  </select>
                </div>
                <span className="ml-auto text-sm text-brand-muted">{filtered.length} of {proposals.length}</span>
              </div>

              <div className="bg-brand-surface border border-brand-border overflow-x-auto" data-testid="proposals-list">
                <table className="w-full text-sm">
                  <thead><tr className="text-brand-tertiary border-b border-brand-border">{["Title", "Client", "Locations", "Opens", "Status", "Created", "Updated", ""].map((h, i) => <th key={h || "a"} className={`font-medium uppercase tracking-[0.1em] text-[11px] px-4 py-3 ${i === 0 ? "text-left px-6" : i === 3 ? "text-right" : "text-left"}`}>{h}</th>)}</tr></thead>
                  <tbody>
                    {filtered.length === 0 && <tr><td colSpan={8} className="px-6 py-10 text-center text-brand-tertiary">{proposals.length === 0 ? "No proposals yet. Click “New Proposal”." : "No proposals match these filters."}</td></tr>}
                    {filtered.map((p) => {
                      const expired = p.status === "published" && isExpired(p.valid_until);
                      return (
                        <tr key={p.id} data-testid={`proposal-row-${p.id}`} className="border-b border-brand-border last:border-0 hover:bg-brand-hover cursor-pointer" onClick={() => navigate(`/proposals/${p.id}`)}>
                          <td className="px-6 py-3.5 text-brand-ink">
                            <span className="inline-flex items-center gap-2">
                              {p.title}
                              {p.pending_requests > 0 && (
                                <span data-testid={`proposal-action-needed-${p.id}`} title="A prospect requested an agreement"
                                  className="inline-flex items-center gap-1 rounded-full bg-brand-gold/20 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-brand-gold">
                                  ● {p.pending_requests} agreement request{p.pending_requests === 1 ? "" : "s"}
                                </span>
                              )}
                            </span>
                          </td>
                          <td className="px-4 py-3.5 text-brand-muted">{p.client_name || "—"}</td>
                          <td className="px-4 py-3.5 text-brand-muted max-w-[220px] truncate">{(p.locations || []).join(", ") || "—"}</td>
                          <td className="px-4 py-3.5 text-right" data-testid={`proposal-opens-${p.id}`}>
                            <span className={`inline-flex items-center gap-1 font-mono text-sm ${p.opens > 0 ? "text-brand-gold" : "text-brand-tertiary"}`}><Eye className="h-3.5 w-3.5" /> {p.opens || 0}</span>
                          </td>
                          <td className="px-4 py-3.5"><span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-1 rounded-full ${expired ? "bg-brand-gold/15 text-brand-gold" : p.status === "published" ? "bg-brand-pos/10 text-brand-pos" : "bg-brand-hover text-brand-tertiary"}`}>{expired ? "Expired" : p.status}</span></td>
                          <td className="px-4 py-3.5 text-brand-muted whitespace-nowrap" data-testid={`proposal-created-${p.id}`}>{fmtDate(p.created_at)}</td>
                          <td className="px-4 py-3.5 text-brand-muted whitespace-nowrap" data-testid={`proposal-updated-${p.id}`}>{fmtDate(p.updated_at)}</td>
                          <td className="px-4 py-3.5 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                            {p.status === "published" && <a href={`/p/${p.share_token}`} target="_blank" rel="noreferrer" data-testid={`proposal-open-${p.id}`} className="p-1.5 inline-flex text-brand-tertiary hover:text-brand-primary"><ExternalLink className="h-4 w-4" /></a>}
                            <button onClick={() => duplicate(p)} data-testid={`proposal-duplicate-${p.id}`} className="p-1.5 text-brand-tertiary hover:text-brand-primary"><Copy className="h-4 w-4" /></button>
                            <button onClick={() => del(p)} data-testid={`proposal-delete-${p.id}`} className="p-1.5 text-brand-tertiary hover:text-brand-neg"><Trash2 className="h-4 w-4" /></button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })()}
        {tab === "catalog" && false && <OfficeCatalog locations={locations} />}
        {tab === "locations" && false && <LocationLibrary locations={locations} reload={loadLocs} />}
        {tab === "slack" && false && user?.role === "admin" && <SlackAlerts />}
      </main>
    </div>
  );
}
